import { useEffect, useRef, type ReactNode } from "react";
import { AnalysisChart } from "./analysis-chart.js";
import { AnalysisContext } from "./analysis-context.js";
import { action, money, timestamp } from "./analysis-format.js";
import type { AnalysisReport } from "./queries.js";

export function AnalysisDeleteDialog({
  children,
  pending,
  onCancel,
  labelledBy = "history-delete-title",
  describedBy = "history-delete-description",
}: {
  readonly children: ReactNode;
  readonly pending: boolean;
  readonly onCancel: () => void;
  readonly labelledBy?: string;
  readonly describedBy?: string;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  return (
    <dialog
      ref={dialogRef}
      className="ai-analysis-history-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      onCancel={(event) => {
        event.preventDefault();
        if (!pending) onCancel();
      }}
    >
      <div className="section-stack">{children}</div>
    </dialog>
  );
}

export function ReportDetail({ report }: { readonly report: AnalysisReport }) {
  const decision = report.decision;
  return (
    <article className="section-stack" aria-label="Analysis report">
      <h3>{report.symbol} report</h3>
      <p className="section-description">
        {report.status.replaceAll("_", " ")} · Scheduled: {timestamp(report.scheduledAt)} · Started:{" "}
        {timestamp(report.startedAt)}
      </p>
      {report.error === undefined ? null : (
        <p className="form-message--error" role="alert">
          {report.error}
        </p>
      )}
      {decision === undefined ? null : (
        <>
          <div className="ai-analysis-decisions">
            <div>
              <span>If you do not own {report.symbol}</span>
              <strong>{action(decision.entryAction)}</strong>
            </div>
            <div>
              <span>If you already own {report.symbol}</span>
              <strong>{action(decision.holdingAction)}</strong>
            </div>
            <div>
              <span>Expected horizon</span>
              <strong>{decision.horizonTradingDays} trading days</strong>
            </div>
          </div>
          {report.status === "completed" ? <AnalysisChart key={report.id} report={report} /> : null}
          <p className="ai-analysis-reason">{decision.reason}</p>
          <AnalysisContext report={report} />
          <div className="ai-analysis-factors">
            {(
              [
                ["Bullish factors", decision.bullishFactors],
                ["Bearish factors", decision.bearishFactors],
                ["Invalidation conditions", decision.invalidationConditions],
              ] as const
            ).map(([title, items]) => (
              <section key={title}>
                <h3>{title}</h3>
                {items.length === 0 ? (
                  <p>None identified.</p>
                ) : (
                  <ul>
                    {items.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
          <p className="field-hint">Referenced evidence: {decision.evidenceIds.join(", ")}</p>
        </>
      )}
      {report.snapshot === undefined ? null : (
        <>
          <p className="field-hint">
            Analysis time: {timestamp(report.snapshot.asOf)} · Latest completed price session:{" "}
            {report.snapshot.lastSessionDate}
          </p>
          <ul className="field-hint">
            {report.snapshot.limitations.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <details>
            <summary>Evidence snapshot</summary>
            <div className="ai-analysis-evidence">
              {report.snapshot.evidence.map((item) => (
                <div key={item.id}>
                  <strong>{item.id}</strong>
                  <p>{item.description}</p>
                  <dl>
                    {Object.entries(item.values).map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>
                          {Number.isInteger(value) ? value.toLocaleString() : value.toFixed(4)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </details>
        </>
      )}
      <p className="field-hint">
        Model: {report.model ?? "—"} · Prompt: {report.promptVersion ?? "—"} · Tokens:{" "}
        {report.inputTokens ?? "—"} input / {report.outputTokens ?? "—"} output · Cost:{" "}
        {report.costUsd === undefined ? "—" : money(report.costUsd)}
        {report.costUsd === undefined && report.reservedCostUsd !== undefined
          ? ` · Reserved: ${money(report.reservedCostUsd)}`
          : ""}
      </p>
    </article>
  );
}
